// FRU-23258: when a visitor leaves the page (Back out of the form, closing the tab, switching apps, an in-app browser closing),
// the browser cancels the page's requests that are still in flight and they fail with status 0. Those cancellations are not
// failures of our service, and reporting each one as a Sentry exception buried the real ones (about 170 a day on ANGULAR-77A).
// This records when the page last showed a sign of leaving, so a status-0 failure can be told apart from one the visitor saw.

export type PageLeavingReason = 'hidden' | 'pagehide' | 'beforeunload' | 'back' | 'frozen';

interface PageLeavingSignals {
  hidden: number;
  pagehide: number;
  beforeunload: number;
  back: number;
  frozen: number;
  // The page came back (visible again, pageshow, resume): leaving signals before this no longer count.
  returned: number;
}

const signals: PageLeavingSignals = { hidden: 0, pagehide: 0, beforeunload: 0, back: 0, frozen: 0, returned: 0 };
let watching = false;

function stamp(key: keyof PageLeavingSignals): void {
  signals[key] = Date.now();
}

/** Record leaving signals from the given window and document. watchPageLeaving() wires the real ones once. */
export function listenForPageLeaving(win: EventTarget, doc: EventTarget & { visibilityState?: string }): void {
  try {
    win.addEventListener('pagehide', () => stamp('pagehide'), true);
    // beforeunload alone is not proof of leaving (the visitor can cancel the leave prompt), so it only counts for a short window.
    win.addEventListener('beforeunload', () => stamp('beforeunload'), true);
    // popstate fires only for the browser's Back and Forward, never for the app's own pushState or replaceState.
    win.addEventListener('popstate', () => stamp('back'), true);
    win.addEventListener('pageshow', () => stamp('returned'), true);
    doc.addEventListener('visibilitychange', () => {
      stamp(doc.visibilityState === 'hidden' ? 'hidden' : 'returned');
    }, true);
    doc.addEventListener('freeze', () => stamp('frozen'), true);
    doc.addEventListener('resume', () => stamp('returned'), true);
  } catch (e) { /* leaving signals are best effort */ }
}

/** Start recording leaving signals. Safe to call more than once; only the first call adds listeners. */
export function watchPageLeaving(): void {
  if (watching || typeof window === 'undefined' || typeof document === 'undefined') { return; }
  watching = true;
  listenForPageLeaving(window, document);
}

/**
 * Why the page looks like it is leaving: hidden right now, or a leaving signal at or after `since` (epoch ms) that the page
 * has not come back from since (a tab switch and back, or a bfcache return, cancels it). A browser Back is reported as is.
 * Returns null when the visitor is still on a visible page and nothing has signalled a leave since then.
 */
export function pageLeavingSince(
  since: number,
  doc: { visibilityState?: string } | null = typeof document !== 'undefined' ? document : null,
): PageLeavingReason | null {
  try {
    if (doc && doc.visibilityState === 'hidden') { return 'hidden'; }
  } catch (e) { /* fall through to the recorded signals */ }
  const order: PageLeavingReason[] = ['pagehide', 'frozen', 'hidden', 'beforeunload', 'back'];
  for (const reason of order) {
    const at = signals[reason];
    if (!at || at < since) { continue; }
    if (reason !== 'back' && at <= signals.returned) { continue; }
    return reason;
  }
  return null;
}

/**
 * Like pageLeavingSince, but only for signs the page itself is going: a browser Back alone does not count, because inside
 * Hope it moves to the previous question and the page stays. A Back that does leave fires pagehide.
 */
export function pageDepartingSince(
  since: number,
  doc: { visibilityState?: string } | null = typeof document !== 'undefined' ? document : null,
): PageLeavingReason | null {
  const reason = pageLeavingSince(since, doc);
  return reason === 'back' ? null : reason;
}

/** Test-only: forget recorded signals between specs. */
export function resetPageLeavingSignals(): void {
  (Object.keys(signals) as Array<keyof PageLeavingSignals>).forEach((key) => { signals[key] = 0; });
}
